/**
 * Save Tab Content for GameMenu
 * Responsive design for all screen sizes
 */

import { MenuTabContent } from '../MenuTabContent';
import { Player } from '@client/entities/Player';
import { getSaveManager, SaveSlotInfo } from '@client/save/SaveManager';

export class SaveTab extends MenuTabContent {
  private saveSlots: SaveSlotInfo[] = [];
  private hoveredSlot: number = -1;
  private mode: 'save' | 'load' = 'save';

  public onSave?: (slot: number) => void;
  public onLoad?: (slot: number) => void;

  constructor(player: Player) {
    super(player);
    this.refreshSlots();
  }

  private refreshSlots(): void {
    const manager = getSaveManager();
    this.saveSlots = manager.getSaveSlots();
  }

  measureSize(_ctx: CanvasRenderingContext2D): { width: number; height: number } {
    return { width: 850, height: 500 };
  }

  update(_deltaTime: number): void {}

  onActivate(): void {
    this.refreshSlots();
    this.hoveredSlot = -1;
  }

  // Get responsive layout
  private getLayout() {
    const { width, height } = this.bounds;
    const padding = Math.min(30, width * 0.03);
    const maxContentWidth = 600;
    const contentWidth = Math.min(maxContentWidth, width - padding * 2);
    const contentX = (width - contentWidth) / 2;

    return {
      padding,
      contentWidth,
      contentX,
      fontSize: Math.max(11, Math.min(14, width / 80)),
      titleSize: Math.max(14, Math.min(18, width / 60)),
      slotHeight: Math.max(65, Math.min(80, height / 7)),
      buttonWidth: Math.min(130, contentWidth * 0.22),
      buttonHeight: Math.min(38, height * 0.06),
    };
  }

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    const { x, y, width, height } = this.bounds;
    const layout = this.getLayout();
    const centerX = x + width / 2;

    // Mode toggle buttons
    const btnW = layout.buttonWidth;
    const btnH = layout.buttonHeight;
    const btnY = y + layout.padding;

    // Save button
    ctx.fillStyle = this.mode === 'save' ? '#2a4a2a' : '#1a1510';
    this.drawRoundedRect(ctx, centerX - btnW - 10, btnY, btnW, btnH, 5);
    ctx.fill();
    ctx.strokeStyle = this.mode === 'save' ? '#2ecc71' : '#3d3228';
    ctx.lineWidth = this.mode === 'save' ? 2 : 1;
    ctx.stroke();

    ctx.fillStyle = this.mode === 'save' ? '#2ecc71' : '#808070';
    ctx.font = `bold ${layout.fontSize}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText('KAYDET', centerX - btnW / 2 - 10, btnY + btnH / 2 + 5);

    // Load button
    ctx.fillStyle = this.mode === 'load' ? '#2a3a4a' : '#1a1510';
    this.drawRoundedRect(ctx, centerX + 10, btnY, btnW, btnH, 5);
    ctx.fill();
    ctx.strokeStyle = this.mode === 'load' ? '#3498db' : '#3d3228';
    ctx.lineWidth = this.mode === 'load' ? 2 : 1;
    ctx.stroke();

    ctx.fillStyle = this.mode === 'load' ? '#3498db' : '#808070';
    ctx.font = `bold ${layout.fontSize}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText('YUKLE', centerX + btnW / 2 + 10, btnY + btnH / 2 + 5);

    // Slots
    const slotW = layout.contentWidth;
    const slotH = layout.slotHeight;
    const slotX = x + layout.contentX;
    const startY = btnY + btnH + 30;
    const slotSpacing = 10;

    for (let i = 0; i < this.saveSlots.length; i++) {
      const slotY = startY + i * (slotH + slotSpacing);
      if (slotY + slotH > y + height - 40) continue; // Don't render if out of bounds

      const slot = this.saveSlots[i];
      const isHovered = this.hoveredSlot === i;

      // Slot background
      ctx.fillStyle = isHovered ? '#2a2520' : '#1a1510';
      this.drawRoundedRect(ctx, slotX, slotY, slotW, slotH, 8);
      ctx.fill();
      ctx.strokeStyle = isHovered ? '#d4af37' : '#3d3228';
      ctx.lineWidth = isHovered ? 2 : 1;
      ctx.stroke();

      // Slot number
      ctx.fillStyle = '#d4af37';
      ctx.font = `bold ${layout.titleSize}px Arial`;
      ctx.textAlign = 'left';
      ctx.fillText(`Slot ${slot.slot + 1}`, slotX + 20, slotY + slotH * 0.35);

      if (!slot.isEmpty) {
        // Has save data
        ctx.fillStyle = '#b0a090';
        ctx.font = `${layout.fontSize}px Arial`;
        ctx.fillText(`${slot.className} - Seviye ${slot.playerLevel}`, slotX + 20, slotY + slotH * 0.6);

        ctx.fillStyle = '#606050';
        ctx.font = `${layout.fontSize - 2}px Arial`;
        ctx.fillText(this.formatDate(slot.timestamp || 0), slotX + 20, slotY + slotH * 0.82);

        // Play time
        ctx.fillStyle = '#808070';
        ctx.textAlign = 'right';
        ctx.fillText(this.formatPlayTime(slot.playTime || 0), slotX + slotW - 120, slotY + slotH * 0.6);
      } else {
        // Empty slot
        ctx.fillStyle = '#505050';
        ctx.font = `${layout.fontSize}px Arial`;
        ctx.fillText('Bos Slot', slotX + 20, slotY + slotH * 0.6);
      }

      // Action button
      const actionBtnW = Math.min(90, slotW * 0.15);
      const actionBtnH = Math.min(40, slotH * 0.55);
      const actionBtnX = slotX + slotW - actionBtnW - 15;
      const actionBtnY = slotY + (slotH - actionBtnH) / 2;
      const canAct = this.mode === 'save' || !slot.isEmpty;

      if (canAct && isHovered) {
        ctx.fillStyle = this.mode === 'save' ? '#1a4a1a' : '#1a3a4a';
        this.drawRoundedRect(ctx, actionBtnX, actionBtnY, actionBtnW, actionBtnH, 5);
        ctx.fill();
        ctx.strokeStyle = this.mode === 'save' ? '#2ecc71' : '#3498db';
        ctx.lineWidth = 1;
        ctx.stroke();

        ctx.fillStyle = this.mode === 'save' ? '#2ecc71' : '#3498db';
        ctx.font = `bold ${layout.fontSize - 1}px Arial`;
        ctx.textAlign = 'center';
        ctx.fillText(this.mode === 'save' ? 'Kaydet' : 'Yukle', actionBtnX + actionBtnW / 2, actionBtnY + actionBtnH / 2 + 4);
      }
    }

    // Hint
    ctx.fillStyle = '#505050';
    ctx.font = `${layout.fontSize - 2}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText('F5: Hizli Kaydet | F9: Hizli Yukle', centerX, y + height - 15);
  }

  handleMouseEvent(e: import('../components/UIComponent').MouseEvent): boolean {
    if (!this.visible) return false;

    const { x, y, width } = this.bounds;
    const layout = this.getLayout();
    const centerX = x + width / 2;

    // Mode buttons
    const btnW = layout.buttonWidth;
    const btnH = layout.buttonHeight;
    const btnY = y + layout.padding;

    if (e.type === 'down' && e.button === 'left') {
      // Save button
      if (e.x >= centerX - btnW - 10 && e.x <= centerX - 10 &&
          e.y >= btnY && e.y <= btnY + btnH) {
        this.mode = 'save';
        return true;
      }

      // Load button
      if (e.x >= centerX + 10 && e.x <= centerX + btnW + 10 &&
          e.y >= btnY && e.y <= btnY + btnH) {
        this.mode = 'load';
        return true;
      }

      // Slot clicks
      const slotW = layout.contentWidth;
      const slotH = layout.slotHeight;
      const slotX = x + layout.contentX;
      const startY = btnY + btnH + 30;
      const slotSpacing = 10;

      for (let i = 0; i < this.saveSlots.length; i++) {
        const slotY = startY + i * (slotH + slotSpacing);
        if (e.x >= slotX && e.x <= slotX + slotW &&
            e.y >= slotY && e.y <= slotY + slotH) {
          const slot = this.saveSlots[i];
          if (this.mode === 'save') {
            if (this.onSave) this.onSave(slot.slot);
            this.refreshSlots();
          } else if (!slot.isEmpty && this.onLoad) {
            this.onLoad(slot.slot);
          }
          return true;
        }
      }
    }

    if (e.type === 'move') {
      this.hoveredSlot = -1;
      const slotW = layout.contentWidth;
      const slotH = layout.slotHeight;
      const slotX = x + layout.contentX;
      const startY = btnY + btnH + 30;
      const slotSpacing = 10;

      for (let i = 0; i < this.saveSlots.length; i++) {
        const slotY = startY + i * (slotH + slotSpacing);
        if (e.x >= slotX && e.x <= slotX + slotW &&
            e.y >= slotY && e.y <= slotY + slotH) {
          this.hoveredSlot = i;
          break;
        }
      }
    }

    return false;
  }

  private formatDate(timestamp: number): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('tr-TR') + ' ' + date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  }

  private formatPlayTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}s ${minutes}d`;
  }
}
